import { useRef, useState } from 'react'
import { prepareImage } from '../../imageFile'
import type { PreparedImage } from '../../imageFile'
import { AddPhotoButton } from '../AddPhotoButton'
import { Glyph } from '../glyphs'
import { ItemImage } from '../ItemArt'

/**
 * The picture at the top of the form.
 *
 * An item that exists gets its photo filed straight away, through the same
 * `AddPhotoButton` as the item page — choosing the cover and deleting one stay
 * on the item page's gallery, where all of them are shown. A new item has no
 * id to file against yet, so the photo is held here (`staged`) and uploaded
 * the moment Save has created it: the part is in somebody's hand right now,
 * which is the cheapest photo it will ever get.
 */
export function EditorPhoto({
  itemId,
  imageId,
  name,
  category,
  staged,
  onStage,
}: {
  /** Null for a new item. */
  itemId: string | null
  imageId: string | null
  name: string
  category: string
  staged: PreparedImage | null
  onStage: (image: PreparedImage | null) => void
}): JSX.Element {
  const input = useRef<HTMLInputElement>(null)
  const [problem, setProblem] = useState<string | null>(null)

  return (
    <div className="ie-photo">
      <span className="ie-photo__frame">
        {staged ? (
          <img className="inv-art inv-art--photo inv-art--cover" src={staged.previewUrl} alt="" />
        ) : (
          <ItemImage imageId={imageId} category={category} name={name} />
        )}
      </span>
      {itemId !== null ? (
        <AddPhotoButton itemId={itemId} label={imageId ? 'Add photo' : 'Take a photo'} className="ie-photo__button" />
      ) : (
        <>
          <input
            ref={input}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={async (e) => {
              const file = e.target.files?.[0]
              e.target.value = ''
              if (!file) return
              const prepared = await prepareImage(file)
              if (!prepared.ok) return setProblem(prepared.reason)
              setProblem(null)
              if (staged) URL.revokeObjectURL(staged.previewUrl)
              onStage(prepared.image)
            }}
          />
          <button type="button" className="ie-photo__button" onClick={() => input.current?.click()}>
            <Glyph name="camera" />
            {staged ? 'Replace photo' : 'Take a photo'}
          </button>
          {staged ? (
            <button
              type="button"
              className="danger ie-photo__button"
              onClick={() => {
                URL.revokeObjectURL(staged.previewUrl)
                onStage(null)
              }}
            >
              Remove photo
            </button>
          ) : null}
        </>
      )}
      {problem ? <span className="ie-field__error">{problem}</span> : null}
    </div>
  )
}
